import moment from 'moment';

export type MetricRow = {
  date: string;
  label: string;
  records: number;
  errors: number;
  size: number;
  recordsHidden?: boolean;
};

export type MetricTotals = {
  records: number;
  errors: number;
  size: number;
};

type RawDailyMetric = Partial<Record<'records' | 'errors' | 'size', number>>;

export type ResourceMetricsData = {
  metrics?: Record<string, RawDailyMetric> | null;
} | null;

const DAY_KEY_FORMAT = 'YYYY-MM-DD';
const LABEL_FORMAT = 'MM/DD';

const toUtcDay = (value: string | Date) => moment.utc(value).startOf('day');

const safeNumber = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

export function buildMetricRows(args: {
  data: ResourceMetricsData;
  start: string | Date;
  end: string | Date;
  hideRecords?: boolean;
}): MetricRow[] {
  const { data, start, end, hideRecords = false } = args;

  const startDay = toUtcDay(start);
  const endDay = toUtcDay(end);

  if (!startDay.isValid() || !endDay.isValid() || endDay.isBefore(startDay)) {
    return [];
  }

  const byDay: Record<string, RawDailyMetric> = {};
  Object.entries(data?.metrics ?? {}).forEach(([key, metric]) => {
    const day = moment.utc(key);
    if (day.isValid()) {
      byDay[day.format(DAY_KEY_FORMAT)] = metric;
    }
  });

  const rows: MetricRow[] = [];
  const cursor = startDay.clone();

  while (!cursor.isAfter(endDay)) {
    const dayKey = cursor.format(DAY_KEY_FORMAT);
    const metric = byDay[dayKey];

    rows.push({
      date: dayKey,
      label: cursor.format(LABEL_FORMAT),
      records: hideRecords ? 0 : safeNumber(metric?.records),
      errors: safeNumber(metric?.errors),
      size: safeNumber(metric?.size),
      ...(hideRecords ? { recordsHidden: true } : {}),
    });

    cursor.add(1, 'day');
  }

  return rows;
}

export function getMetricTotals(rows: MetricRow[]): MetricTotals {
  return rows.reduce<MetricTotals>(
    (acc, row) => {
      acc.records += row.records;
      acc.errors += row.errors;
      acc.size += row.size;
      return acc;
    },
    { records: 0, errors: 0, size: 0 },
  );
}

export type SortDirection = 'asc' | 'desc';

export function sortMetricRowsByDate(rows: MetricRow[], direction: SortDirection): MetricRow[] {
  const sorted = [...rows].sort((a, b) => a.date.localeCompare(b.date));
  return direction === 'desc' ? sorted.reverse() : sorted;
}

export const isZeroRow = (row: MetricRow): boolean =>
  row.records === 0 && row.errors === 0 && row.size === 0;
